import React, { useCallback, useEffect, useMemo, useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { MediaGallery } from "@/components/MediaGallery";
import { VideoGallerySection } from "@/components/VideoGallerySection";
import {
    CATEGORIES,
    SCOPE_CATEGORIES,
    SCOPE_FILTER,
    SCOPE_META,
    SCOPE_SUBFILTER,
} from "@/lib/data";
import { usePortfolio, usePortfolioVideo } from "@/lib/content";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";

/**
 * Portfolio page — Phase C mobile-first re-architecture.
 *
 * Mobile (<768 px): a hero scope-picker leads the page. Visitors see three
 * large editorial tiles ("All Work", "Life & Legacy", "Brand & Business")
 * before any category filter chips appear — this solves the bounce-rate
 * issue where mobile users had to scroll past 25+ photos and 10+ chips just
 * to navigate. The active scope's category chips and masonry gallery render
 * directly below.
 *
 * Desktop (≥768 px): the scope tiles collapse into a tighter horizontal
 * row at the top of the page (preserving the desktop information density),
 * with category chips and the gallery below.
 *
 * Image viewing now uses the unified MediaGallery component — masonry +
 * PhotoSwipe v5 lightbox with pinch zoom, swipe, and keyboard nav. The
 * legacy custom Radix Dialog lightbox has been retired.
 *
 * Deep-linking: `?category=<label>` selects the active filter on mount;
 * changing the filter updates the URL so links can be shared / opened
 * from email campaigns directly on the right view.
 */

export default function Portfolio() {
    const { scope } = useParams(); // undefined | "life-legacy" | "brand-business"
    const validScope = scope === "life-legacy" || scope === "brand-business" ? scope : "all";

    const PORTFOLIO = usePortfolio();
    const meta = SCOPE_META[validScope];
    const cats = validScope === "all" ? CATEGORIES : SCOPE_CATEGORIES[validScope];
    const scopedItems = useMemo(
        () => (validScope === "all" ? PORTFOLIO : PORTFOLIO.filter(SCOPE_FILTER[validScope])),
        [validScope, PORTFOLIO],
    );

    // ----- Deep-link support: ?category=<label> on mount -----------------
    const [searchParams, setSearchParams] = useSearchParams();
    const initialCategory = useMemo(() => {
        const fromUrl = searchParams.get("category");
        if (fromUrl && cats.includes(fromUrl)) return fromUrl;
        return "All";
    }, [searchParams, cats]);

    const [active, setActive] = useState(initialCategory);

    // Reset filter when scope changes (and respect ?category= on each scope)
    useEffect(() => {
        const fromUrl = searchParams.get("category");
        setActive(fromUrl && cats.includes(fromUrl) ? fromUrl : "All");
        // intentionally only re-runs when scope changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validScope]);

    // Push the active filter into the URL so deep-linking works in reverse —
    // users can share / bookmark the current view.
    const onSelectCategory = useCallback(
        (label) => {
            setActive(label);
            const next = new URLSearchParams(searchParams);
            if (label === "All") {
                next.delete("category");
            } else {
                next.set("category", label);
            }
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const items = useMemo(() => {
        if (active === "All") return scopedItems;
        if (validScope === "all") return scopedItems.filter((p) => p.category === active);
        const fn = SCOPE_SUBFILTER[validScope];
        return fn ? scopedItems.filter((p) => fn(p, active)) : scopedItems;
    }, [active, scopedItems, validScope]);

    // Map a portfolio item to the best-fitting session type for the inquire
    // form. Used by the lightbox CTA so visitors land on /inquire with the
    // right session type already selected.
    const inquiryTypeFor = (p) => {
        if (Array.isArray(p.brandTags) && p.brandTags.length) return "Brand Story Session";
        if (Array.isArray(p.lifeTags) && p.lifeTags.length) {
            if (p.lifeTags.includes("Personal Portraits")) return "Personal Portrait Session";
            if (p.lifeTags.includes("Family Stories")) return "Family / Couples";
            if (p.lifeTags.includes("Couples")) return "Family / Couples";
            if (p.lifeTags.includes("Proposals & Engagements")) return "Proposal / Engagement";
            if (p.lifeTags.includes("Private Celebrations")) return "Private Event / Celebration";
            return "Story Session";
        }
        switch (p.category) {
            case "Branding":
            case "Business":
                return "Brand Story Session";
            case "Family":
            case "Portraits":
                return "Personal Portrait Session";
            case "Events":
                return "Private Event / Celebration";
            case "Editorial":
                return "Story Session";
            default:
                return "Story Session";
        }
    };

    // Items transformed for MediaGallery (varying ratios for editorial flow).
    const galleryItems = useMemo(
        () =>
            items.map((p, i) => {
                const ratios = [4 / 5, 1, 4 / 5, 3 / 4, 4 / 5, 1];
                return {
                    id: p.id,
                    src: p.image,
                    alt: p.alt || p.title,
                    caption: p.title,
                    ratio: ratios[i % ratios.length],
                    inquiryType: inquiryTypeFor(p),
                };
            }),
        [items],
    );

    return (
        <div className="pt-24 sm:pt-28" data-testid={`portfolio-page-${validScope}`}>
            <Seo {...SEO.portfolio} />

            {/* ============ HERO ============ */}
            <Section spacing="tight">
                <Kicker>{meta.kicker}</Kicker>
                <SerifTitle className="mt-3">{meta.title}</SerifTitle>
                <p className="mt-5 max-w-[64ch] text-sm leading-[1.85] text-muted-foreground">
                    {meta.copy}
                </p>

                {/* Inline scope switcher — small chip row, no large duplicate tiles.
                    Filter chips below the divider remain the primary navigation. */}
                <div className="mt-7 flex flex-wrap items-center gap-2 text-[11px] tracking-[0.18em] uppercase">
                    <span className="text-muted-foreground mr-1">View:</span>
                    <ScopeChip to="/portfolio" label="All Work" active={validScope === "all"} testId="portfolio-scope-all-work" />
                    <ScopeChip to="/portfolio/life-legacy" label="Life & Legacy" active={validScope === "life-legacy"} testId="portfolio-scope-life-legacy" />
                    <ScopeChip to="/portfolio/brand-business" label="Brand & Business" active={validScope === "brand-business"} testId="portfolio-scope-brand-business" />
                </div>
            </Section>

            {/* ============ CATEGORY FILTER CHIPS ============ */}
            <Section spacing="none">
                <div
                    data-testid="portfolio-filters"
                    className="flex flex-wrap gap-2 pb-6 border-b border-border"
                >
                    {cats.map((c) => (
                        <button
                            key={c}
                            data-testid={`portfolio-filter-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            onClick={() => onSelectCategory(c)}
                            className={cn(
                                "px-3.5 h-9 rounded-full text-[11px] tracking-[0.18em] uppercase border transition-colors duration-200",
                                active === c
                                    ? "border-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-oxblood))] bg-[hsl(var(--lux-oxblood))]/[0.06]"
                                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40",
                            )}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </Section>

            {/* ============ MASONRY GALLERY + LIGHTBOX ============ */}
            <Section spacing="normal">
                {items.length === 0 ? (
                    <p
                        className="text-center text-sm text-muted-foreground py-10"
                        data-testid="portfolio-empty"
                    >
                        No work to display in this filter yet.
                    </p>
                ) : (
                    <MediaGallery
                        key={`${validScope}-${active}`}
                        items={galleryItems}
                        desktopColumns={3}
                        initialCount={12}
                        incrementCount={12}
                        defaultInquiryType={meta.ctaRequestType || "Story Session"}
                        galleryId={`portfolio-gallery-${validScope}`}
                    />
                )}

                {/* Closing CTA — scope-aware */}
                {validScope === "life-legacy" && (
                    <div
                        className="mt-16 border-t border-border pt-12 text-center"
                        data-testid="portfolio-life-legacy-final-cta"
                    >
                        <Kicker>The next step</Kicker>
                        <SerifTitle className="mt-3 max-w-[26ch] mx-auto">
                            Ready to create images for this season of your life?
                        </SerifTitle>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[58ch] mx-auto">
                            Sessions are guided from concept to final gallery — guidance on posing,
                            styling, location, and creative direction included.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            <Link
                                to={`/inquire?type=${encodeURIComponent(meta.ctaRequestType)}`}
                                data-testid="portfolio-life-legacy-cta"
                                className="btn-request"
                            >
                                Request Availability
                            </Link>
                            <Link
                                to={meta.ctaBackTo}
                                className="btn-request btn-request--sm btn-request--ghost inline-flex items-center"
                            >
                                {meta.ctaBackLabel} <ArrowUpRight className="ml-2 h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>
                )}

                {validScope === "brand-business" && (
                    <div
                        className="mt-16 border-t border-border pt-12 text-center"
                        data-testid="portfolio-brand-business-final-cta"
                    >
                        <Kicker>The next step</Kicker>
                        <SerifTitle className="mt-3 max-w-[28ch] mx-auto">
                            Ready to make your business look more trusted and premium?
                        </SerifTitle>
                        <p className="mt-5 text-sm text-muted-foreground max-w-[60ch] mx-auto">
                            Every session is planned around your goals — what your clients need to
                            see, where the images will live, and the credibility you need to build.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                            <Link
                                to={`/inquire?type=${encodeURIComponent(meta.ctaRequestType)}`}
                                data-testid="portfolio-brand-business-cta"
                                className="btn-request"
                            >
                                Build Your Brand Presence
                            </Link>
                            <Link
                                to={meta.ctaBackTo}
                                className="btn-request btn-request--sm btn-request--ghost inline-flex items-center"
                            >
                                {meta.ctaBackLabel} <ArrowUpRight className="ml-2 h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>
                )}
            </Section>

            {/* Phase 15: VIDEO (auto-hides until portfolio-video Sanity
              * gallery is published with ≥1 clip). Renders only on the
              * unfiltered /portfolio "all" scope — scoped routes have
              * their own pages and would compete with the dedicated
              * life-video / business-video sections on those pages.
              * Rendered OUTSIDE the parent Section so the VideoGallery-
              * Section's own <Section> wrapper provides the correct
              * single layer of vertical rhythm. */}
            {validScope === "all" && <PortfolioVideoSlot />}
        </div>
    );
}

// Phase 15 — slug-driven portfolio video section. Auto-hides until the
// `portfolio-video` Sanity instance is published with ≥1 clip. Renders
// inside the parent <Section> (so it inherits the portfolio container
// width + the page's CTA-friendly spacing) — pass a transparent class
// so it doesn't double-band the background.
function PortfolioVideoSlot() {
    const items = usePortfolioVideo();
    return (
        <VideoGallerySection
            items={items}
            testId="portfolio-video"
            galleryId="portfolio-video-gallery"
            inquiryType="Portfolio Video"
            desktopColumns={3}
            className="border-t border-border"
        />
    );
}


// ----- ScopeChip (small inline chip used for the View: pill row) ---------
const ScopeChip = ({ to, label, active, testId }) => (
    <Link
        to={to}
        data-testid={testId}
        aria-current={active ? "page" : undefined}
        className={cn(
            "px-3.5 h-9 inline-flex items-center rounded-full border transition-colors",
            active
                ? "border-foreground text-foreground"
                : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40",
        )}
    >
        {label}
    </Link>
);
